import { zodResolver } from '@hookform/resolvers/zod';
import {
  AmountUnitTypeEnum,
  DiscountDurationEnum,
  DiscountLifeCyclePolicyEnum,
  DiscountSchema,
  DiscountStatusEnum,
  IAccountRespSchema,
  IDiscount,
  IOfferingRes,
  IProductResSchema,
  IRateResBaseSchema,
  IRateResSchema,
  ProductTypeEnum,
} from '@monetize/types/app';
import pick from 'lodash/pick';
import toString from 'lodash/toString';
import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import type { z } from 'zod';
import { handleApiErrorToast } from '../../../api/axios';
import {
  doGetOfferingById,
  doGetOfferingRate,
  doGetProductById,
} from '../../../api/productCatalogService';
import {
  useCreateEntity,
  useGetById,
  useUpdateEntity,
} from '../../../api/queryUtils';
import { AccountSelect } from '../../../components/Account/AccountSelect';
import {
  MAmountWithUnitTypeInput,
  MBox,
  MButton,
  MCheckbox,
  MCustomNumberInput,
  MDivider,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MPageContainer,
  MPageHeader,
  MPageLoader,
  MText,
} from '../../../components/Monetize';
import { DatePicker } from '../../../components/Monetize/DatePicker/DatePicker';
import { MainSearchInput } from '../../../components/Monetize/MCustomSelect/components/MainSearchInput';
import { MCustomMultiSelect } from '../../../components/Monetize/MCustomSelect/MCustomMultiSelect';
import { MCustomSelect } from '../../../components/Monetize/MCustomSelect/MCustomSelect';
import {
  useCustomSelectValue,
  useCustomSelectValues,
} from '../../../components/Monetize/MCustomSelect/useCustomSelectValue';
import { DISCOUNTS, ROUTES } from '../../../constants';
import { DISCOUNT_STATUS_ENUM_DISPLAY } from '../../../constants/discounts';
import { STATUS_PLACEHOLDER } from '../../../constants/placeholders';
import { DISCOUNT_LIST } from '../../../constants/routes';
import { useCtrlEnterHotkey } from '../../../hooks/useHotkeys';
import { useSettingsReadOnlyRule } from '../../../hooks/useSettingsReadOnlyRule';
import { sortAlphabetically } from '../../../utils';
import { ItemWithStatus } from './components/ItemWithStatus';

type DiscountFormInput = z.input<typeof DiscountSchema>;

export const DiscountForm: FC = () => {
  const navigate = useNavigate();
  const { discountId = '' } = useParams();
  const [discountType, setDiscountType] = useState<AmountUnitTypeEnum>(
    AmountUnitTypeEnum.PERCENTAGE,
  );

  const [offeringProducts, setOfferingProducts] = useState<IProductResSchema[]>(
    [],
  );
  const [offeringRates, setOfferingRates] = useState<IRateResBaseSchema[]>([]);

  const {
    data: discount,
    isLoading: isDiscountLoading,
    isError,
  } = useGetById<IDiscount>('productCatalogDiscounts', discountId || '', {
    enabled: !!discountId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (discount) {
      const formData = pick(discount, Object.keys(DiscountSchema.shape));
      const hasExcludeFromRenewal = !!formData.lifecyclePolicies?.includes(
        DiscountLifeCyclePolicyEnum.EXCLUDE_FROM_RENEWAL,
      );
      reset({
        ...formData,
        discountAmount:
          formData.discountAmount !== null ? formData.discountAmount : null,
        durationMonths:
          formData.durationMonths !== null ? formData.durationMonths : 0,
        maxUses: formData.maxUses,
        startDate: formData.startDate,
        endDate: formData.endDate,
        excludeFromRenewal: hasExcludeFromRenewal,
      });
      // validate fields
      trigger();
    } else if (isError) {
      navigate(ROUTES.DISCOUNT_LIST, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discount, isError, navigate]);

  const doCreateDiscount = useCreateEntity('productCatalogDiscountsV2', {
    onSuccess: () => navigate(ROUTES.DISCOUNT_LIST),
    onError: (error) => {
      handleApiErrorToast(error);
    },
  });

  const doUpdateDiscount = useUpdateEntity('productCatalogDiscountsV2', {
    onSuccess: () => navigate(ROUTES.DISCOUNT_LIST),
    onError: (error) => {
      handleApiErrorToast(error);
    },
  });

  const isDiscountLocked = !!discount?.locked;

  const isSaveLoading =
    doCreateDiscount.isPending || doUpdateDiscount.isPending;

  const {
    trigger,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty, isValid },
  } = useForm<DiscountFormInput>({
    resolver: zodResolver(DiscountSchema),
    mode: 'onChange',
    defaultValues: {
      autoRemove: false,
      status: discount?.status || DiscountStatusEnum.ACTIVE,
      durationMonths: discount?.durationMonths || 0,
      durationType: discount?.durationType || DiscountDurationEnum.UNLIMITED,
      discountAmount:
        discount?.discountAmount !== null &&
        discount?.discountAmount !== undefined
          ? discount.discountAmount
          : '',
    },
    reValidateMode: 'onChange',
  });

  const selectedDurationType = watch('durationType');
  const selectedDurationMonth = watch('durationMonths');

  useEffect(() => {
    if (
      selectedDurationType !== DiscountDurationEnum.LIMITED &&
      selectedDurationMonth
    ) {
      setValue('durationMonths', null);
    }
  }, [selectedDurationMonth, selectedDurationType, setValue]);

  useCtrlEnterHotkey(() => {
    handleSubmit(onSubmit)();
  });

  const onSubmit = async (data: DiscountFormInput) => {
    const payload = {
      ...data,
      discountType,
      discountAmount: Number(data.discountAmount),
      durationMonths:
        selectedDurationType === 'LIMITED' ? data.durationMonths : 0,
      maxUses: data.maxUses,
      startDate: data.startDate,
      endDate: data.endDate,
      currency: 'USD', // TODO-no options to choose in FE. will be implemented later
    };

    if (!discountId) {
      doCreateDiscount.mutate(payload);
    } else {
      doUpdateDiscount.mutate({
        id: discountId,
        payload: { id: discountId, ...payload },
      });
    }
  };

  const onChangeDiscountType = (disType: AmountUnitTypeEnum) => {
    setValue('discountType', disType, { shouldDirty: true }); // only used to set the form's dirty state, the value in the form state is not used
    setDiscountType(disType);
  };

  const handleExcludeFromRenewalChange = (checked: boolean) => {
    setValue(
      'lifecyclePolicies',
      checked ? [DiscountLifeCyclePolicyEnum.EXCLUDE_FROM_RENEWAL] : [],
    );
  };

  useEffect(() => {
    // duration type should alway be ONETIME when discount type FLAT is selected.
    if (discountType === AmountUnitTypeEnum.FLAT) {
      setValue('durationType', DiscountDurationEnum.ONE_TIME);
    }
  }, [discountType, setValue]);

  const watchScopeOfferingId = watch('scopeOfferingId');
  const watchScopeProductIds = watch('scopeProductIds');
  const watchScopeRateIds = watch('scopeRateIds');

  const {
    internalValue: internalScopeOffering,
    isLoading: isLoadingScopeOffering,
    onInternalValueChange: onInternalScopeOfferingChange,
  } = useCustomSelectValue<IOfferingRes>({
    value: watchScopeOfferingId ?? '',
    setValue: (value) => {
      setValue('scopeOfferingId', value, { shouldDirty: true });
    },
    getOneById: doGetOfferingById,
  });

  const {
    internalValue: internalScopeProducts,
    isLoading: isLoadingScopeProducts,
    onInternalValueChange: onInternalScopeProductsChange,
  } = useCustomSelectValues<IProductResSchema>({
    value: watchScopeProductIds ?? [],
    setValue: (value) => {
      setValue('scopeProductIds', value, { shouldDirty: true });
    },
    getByIds: async (ids: string[]) => {
      if (ids.length === 0) {
        return [];
      }
      const results = await Promise.all(ids.map((id) => doGetProductById(id)));
      return results;
    },
  });

  const {
    internalValue: internalScopeRates,
    isLoading: isLoadingScopeRates,
    onInternalValueChange: onInternalScopeRatesChange,
  } = useCustomSelectValues<IRateResSchema>({
    value: watchScopeRateIds ?? [],
    setValue: (value) => {
      setValue('scopeRateIds', value, { shouldDirty: true });
    },
    getByIds: async (ids: string[]) => {
      if (ids.length === 0) {
        return [];
      }
      const results = await Promise.all(ids.map((id) => doGetOfferingRate(id)));
      return results;
    },
  });

  useEffect(() => {
    if (watchScopeOfferingId && internalScopeOffering) {
      const sortedProducts = [...internalScopeOffering.products].sort((a, b) =>
        sortAlphabetically('name')(a, b),
      );

      const sortedRates = [...internalScopeOffering.rates].sort((a, b) =>
        sortAlphabetically('name')(a, b),
      );

      setOfferingProducts(sortedProducts ?? []);
      setOfferingRates(sortedRates ?? []);
    }
  }, [watchScopeOfferingId, internalScopeOffering]);

  const { isReadOnly, inputVariant } = useSettingsReadOnlyRule();
  const headerTitle = isReadOnly
    ? 'View Discount'
    : !discountId
      ? 'New Discount'
      : 'Edit Discount';

  const isDataLoading = discountId && isDiscountLoading;

  return (
    <MPageContainer alignItems="stretch" enableAccountSearch>
      <MPageHeader
        hasBackButton
        title={headerTitle}
        id={discount?.id}
        copyUrl
        status={
          discount?.status ? DISCOUNT_STATUS_ENUM_DISPLAY[discount?.status] : ''
        }
        recordAuditInfo={discount}
        entityJson={discount}
      >
        {!isReadOnly && (
          <MButton
            form="discount-form"
            variant="primary"
            isLoading={isSaveLoading}
            isDisabled={!isDirty || !isValid || isSaveLoading}
            type="submit"
          >
            Save
          </MButton>
        )}
      </MPageHeader>

      {isDataLoading ? (
        <MFlex justify="center" grow={1} height={'100%'}>
          <MPageLoader height="auto" />
        </MFlex>
      ) : (
        <MBox width="100%" py="4" pl={8}>
          <form id="discount-form" onSubmit={handleSubmit(onSubmit)}>
            <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
              <MGridItem colSpan={6}>
                <MFormField error={errors.name} label="Name" isRequired>
                  <Controller
                    name="name"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <MInput
                        maxLength={40}
                        isDisabled={isDiscountLocked || isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        {...field}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>

              <MGridItem colSpan={3}>
                <MFormField error={errors.status} label="Status" isRequired>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <MCustomSelect
                        placeholder={STATUS_PLACEHOLDER}
                        itemTitle="name"
                        itemValue="value"
                        items={DISCOUNTS.DISCOUNT_STATUS}
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        {...field}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>

              <MGridItem colSpan={6}>
                <MFormField
                  error={errors.description}
                  label="Description"
                  skipLabel
                >
                  <Controller
                    name="description"
                    control={control}
                    render={({ field: { value, ...field } }) => (
                      <MInput
                        maxLength={80}
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        value={value || ''}
                        {...field}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>

              <MGridItem colSpan={12} mt="2">
                <MText fontWeight="semibold" fontSize="lg">
                  Discount
                </MText>
                <MDivider mt="4" borderColor="tGray.back" />
              </MGridItem>

              <MGridItem colSpan="auto" minWidth="6rem">
                <MFormField
                  error={errors.discountAmount}
                  label="Amount"
                  isRequired
                >
                  <Controller
                    name="discountAmount"
                    control={control}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <MAmountWithUnitTypeInput
                        placeholder="10"
                        amountUnitType={discountType}
                        value={toString(value)}
                        onChangeAmountUnitType={onChangeDiscountType}
                        isDisabled={isDiscountLocked || isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        usePortal
                        onChange={(valueAsString: string) => {
                          if (valueAsString === '') {
                            onChange(null);
                          } else {
                            onChange(valueAsString);
                          }
                        }}
                        {...rest}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>

              <MGridItem colSpan={3} ml="-1">
                <MFormField error={errors.discountCode} label="Discount Code">
                  <Controller
                    name="discountCode"
                    control={control}
                    defaultValue=""
                    render={({ field: { value, ...field } }) => (
                      <MInput
                        maxLength={40}
                        placeholder="SAVE10%"
                        isDisabled={isDiscountLocked || isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        value={value || ''}
                        {...field}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>

              <MGridItem colSpan={12} mt="2">
                <MText fontWeight="semibold" fontSize="lg">
                  Scope of Discount
                </MText>
                <MDivider mt="4" borderColor="tGray.back" />
              </MGridItem>

              <MGridItem colSpan={6}>
                <MFormField error={errors.scopeOfferingId} label="Offering">
                  <Controller
                    name="scopeOfferingId"
                    control={control}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <MCustomSelect
                        placeholder="Search Offerings"
                        endpoint="/api/offerings"
                        itemTitle="name"
                        itemValue="id"
                        clearable
                        useMainInputAsSearchInput
                        MainInputComponent={MainSearchInput}
                        transformDataFromApi={(data) => {
                          const offeringList = Array.isArray(data)
                            ? data.sort((a, b) =>
                                sortAlphabetically('name')(a, b),
                              )
                            : [];
                          return offeringList;
                        }}
                        value={internalScopeOffering}
                        onChange={(selectedOffering) => {
                          onInternalScopeOfferingChange(selectedOffering);
                          setOfferingProducts(selectedOffering.products);
                          setValue('scopeProductIds', []);
                          setValue('scopeRateIds', []);
                        }}
                        loading={isLoadingScopeOffering}
                        returnItem
                        isDisabled={
                          isDiscountLocked ||
                          isSaveLoading ||
                          isReadOnly ||
                          isLoadingScopeOffering
                        }
                        renderItemContent={({ item }) => (
                          <ItemWithStatus
                            name={item.name}
                            entityId={item.id}
                            status={item.status}
                          />
                        )}
                        {...rest}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>

              <MGridItem colSpan={6}>
                <MFormField error={errors.scopeProductIds} label="Products">
                  <Controller
                    name="scopeProductIds"
                    control={control}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <MCustomMultiSelect
                        clearable
                        multiple
                        returnItem
                        placeholder="Search Products"
                        // Conditionally use API-based or static items approach
                        {...(watchScopeOfferingId
                          ? {
                              items: offeringProducts,
                              showQueryInput: offeringProducts.length > 3,
                            }
                          : {
                              endpoint: '/api/products',
                              transformDataFromApi: (data) => {
                                const productList = Array.isArray(data)
                                  ? data
                                      .sort((a, b) =>
                                        sortAlphabetically('name')(a, b),
                                      )
                                      .filter(
                                        (product) =>
                                          product.productType !==
                                          ProductTypeEnum.DISCOUNT,
                                      )
                                  : [];

                                return productList;
                              },
                            })}
                        useMainInputAsSearchInput
                        MainInputComponent={MainSearchInput}
                        itemTitle="name"
                        itemValue="id"
                        closeButtonText="Apply"
                        value={internalScopeProducts}
                        onChange={(selectedProducts) => {
                          onInternalScopeProductsChange(selectedProducts);
                        }}
                        onClear={() => {
                          setValue('scopeProductIds', [], {
                            shouldDirty: true,
                          });
                        }}
                        loading={
                          isLoadingScopeProducts || isLoadingScopeOffering
                        }
                        isDisabled={
                          isDiscountLocked ||
                          isSaveLoading ||
                          isReadOnly ||
                          isLoadingScopeProducts ||
                          isLoadingScopeOffering
                        }
                        renderItemContent={({ item }) => (
                          <ItemWithStatus
                            name={item.name}
                            entityId={item.id}
                            status={item.status}
                          />
                        )}
                        {...rest}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>

              {watchScopeOfferingId && (
                <MGridItem colSpan={12}>
                  <MFormField
                    error={errors.scopeRateIds}
                    label="Rates"
                    maxW="53%"
                  >
                    <Controller
                      name="scopeRateIds"
                      control={control}
                      render={({ field: { value, onChange, ...rest } }) => (
                        <MCustomMultiSelect
                          clearable
                          multiple
                          returnItem
                          showQueryInput={offeringRates.length > 3}
                          placeholder="Select Rates"
                          items={offeringRates}
                          itemTitle="name"
                          itemValue="id"
                          closeButtonText="Apply"
                          value={internalScopeRates}
                          useMainInputAsSearchInput
                          MainInputComponent={MainSearchInput}
                          onChange={(selectedRates) => {
                            onInternalScopeRatesChange(selectedRates);
                          }}
                          onClear={() => {
                            setValue('scopeRateIds', [], {
                              shouldDirty: true,
                            });
                          }}
                          loading={
                            isLoadingScopeOffering || isLoadingScopeRates
                          }
                          isDisabled={
                            isDiscountLocked ||
                            isSaveLoading ||
                            isReadOnly ||
                            isLoadingScopeOffering ||
                            isLoadingScopeRates
                          }
                          renderItemContent={({ item }) => (
                            <ItemWithStatus
                              name={item.name}
                              entityId={item.id}
                              status={item.status}
                            />
                          )}
                          {...rest}
                        />
                      )}
                    />
                  </MFormField>
                </MGridItem>
              )}

              <MGridItem colSpan={6} mt="1">
                <MFormField label="Account">
                  <Controller
                    name="accountId"
                    control={control}
                    render={({ field: { onChange, value, ...rest } }) => (
                      <AccountSelect
                        placeholder="Search Accounts"
                        onClearAccountValue={() =>
                          setValue('accountId', '', { shouldDirty: true })
                        }
                        value={value || ''}
                        {...rest}
                        onChange={(event) => {
                          const accountId =
                            typeof event === 'string'
                              ? event
                              : (event as IAccountRespSchema)?.id || '';
                          onChange(accountId);
                        }}
                        isDisabled={isDiscountLocked || isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        returnItem={false}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem colSpan={12} mt="6">
                <MText fontWeight="semibold" fontSize="lg">
                  Validity
                </MText>
                <MDivider mt="3" borderColor="tGray.back" />
              </MGridItem>

              <MGridItem colSpan={3}>
                <MFormField error={errors.startDate} label="Start Date">
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        clearable
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>

              <MGridItem colSpan={3}>
                <MFormField error={errors.endDate} label="End Date">
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        clearable
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>

              <MGridItem colSpan={3}>
                <MFormField error={errors.maxUses} label="Max Applications">
                  <Controller
                    name="maxUses"
                    control={control}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <MCustomNumberInput
                        value={value ? toString(value) : ''}
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        onChange={(
                          valueAsString: string,
                          valueAsNumber: number,
                        ) => onChange(valueAsNumber)}
                        {...rest}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>

              <MGridItem colSpan={12}>
                <MFormField error={errors.autoRemove}>
                  <Controller
                    name="autoRemove"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MCheckbox
                        isChecked={!!value}
                        {...rest}
                        fontWeight="normal"
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                      >
                        Auto remove this discount after the end date
                      </MCheckbox>
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem colSpan={12}>
                <MFormField error={errors.excludeFromRenewal}>
                  <Controller
                    name="excludeFromRenewal"
                    control={control}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <MCheckbox
                        isChecked={value as boolean}
                        {...rest}
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        onChange={(event) => {
                          onChange(event);
                          const { checked } = event.target;
                          handleExcludeFromRenewalChange(checked);
                        }}
                      >
                        Remove this discount upon renewal
                      </MCheckbox>
                    )}
                  />
                </MFormField>
              </MGridItem>
            </MGrid>
          </form>
        </MBox>
      )}
    </MPageContainer>
  );
};
